import {use} from 'react'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import {needsInput} from '@github-ui/ahp-relay/ahp-session-selectors'

import type {AhpSessionIdentifiers, DebugEvent, ProvisioningKind, RuntimeStatus} from '../types'
import {DebugEventsAppendContext, DebugEventsDataContext} from '@github-ui/copilot-shared-ui/hooks/use-debug-events'
import {ModeSelectionContext, ModeUpdateContext, type ModePickerState} from './ModeContext'
import {
  NativeAhpSessionContext,
  ThreadTitleActionsContext,
  ThreadTitleStateContext,
  type ThreadTitleActions,
  type ThreadTitleState,
} from '@github-ui/copilot-shared-ui/contexts/NativeAhpContext'
import {
  useNativeAhpTimelineFallbackState as useSharedNativeAhpTimelineFallbackState,
  useNativeAhpTimelineState as useSharedNativeAhpTimelineState,
} from '@github-ui/copilot-shared-ui/hooks/use-native-ahp-timeline-state'
import {ProvisioningKindContext, RuntimeStatusContext} from './RuntimeContext'

export function useRuntimeStatus(): RuntimeStatus {
  const value = use(RuntimeStatusContext)
  const establishedSession = useOptionalEstablishedAhpSession()
  const establishedView = useOptionalEstablishedAhpSessionViewState()
  if (establishedSession) {
    if (establishedView.error) return 'error'
    if (establishedView.state && needsInput(establishedView.state)) return 'waiting-for-confirmation'
    if (establishedView.state?.activeTurn) return 'streaming'
    return 'idle'
  }
  if (value === null) throw new Error('useRuntimeStatus must be used within AhpProvider')
  return value
}

export function useProvisioningKind(): ProvisioningKind | undefined {
  return use(ProvisioningKindContext)
}

export function useDebugEvents(): DebugEvent[] {
  const value = use(DebugEventsDataContext)
  if (!value) throw new Error('useDebugEvents must be used within AhpProvider')
  return value
}

export function useAppendDebugEvent(): (event: DebugEvent) => void {
  const value = use(DebugEventsAppendContext)
  if (!value) throw new Error('useAppendDebugEvent must be used within AhpProvider')
  return value
}

export function useNativeAhpTimelineState() {
  return useSharedNativeAhpTimelineState()
}

export function useNativeAhpTimelineFallbackState() {
  return useSharedNativeAhpTimelineFallbackState()
}

export function useNativeAhpSession() {
  const establishedSession = useOptionalEstablishedAhpSession()
  const value = use(NativeAhpSessionContext)
  if (value === null) throw new Error('useNativeAhpSession must be used within AhpProvider')
  const identity = establishedSession?.identity
  const establishedIdentity =
    identity?.taskId && identity.environmentId && identity.sessionId
      ? ({
          taskId: identity.taskId,
          environmentId: identity.environmentId,
          sessionId: identity.sessionId,
          ...(identity.channel ? {channel: identity.channel} : {}),
        } satisfies AhpSessionIdentifiers)
      : undefined
  return establishedIdentity ?? value
}

export function useThreadTitleState(): ThreadTitleState {
  const value = use(ThreadTitleStateContext)
  if (!value) throw new Error('useThreadTitleState must be used within AhpProvider')
  return value
}

export function useThreadTitleActions(): ThreadTitleActions {
  const value = use(ThreadTitleActionsContext)
  if (!value) throw new Error('useThreadTitleActions must be used within AhpProvider')
  return value
}

export function useModePicker(): ModePickerState {
  const onUpdateMode = use(ModeUpdateContext)
  if (!onUpdateMode) throw new Error('useModePicker must be used within AhpProvider')
  return {selectedMode: use(ModeSelectionContext), onUpdateMode}
}
